'use client'

import { Dispatch, SetStateAction, useEffect } from 'react'

type Normalize<T> = (value: string | number | string[] | number[] | undefined) => T

/**
 * Syncs a default value into selected state when the default value changes.
 * Uses a serialized (JSON) representation of the normalized value as the effect
 * dependency so updates only run when the value actually changes, not on every render.
 *
 * @template T - The type of the selected state after normalization
 * @param defaultValue - Raw default value (string, number, or array of either; may be undefined)
 * @param setSelected - React state setter for the selected value
 * @param normalize - Function that converts the raw default value to type T
 */
export const useSyncDefaultValue = <T>(
    defaultValue: string | number | string[] | number[] | undefined,
    setSelected: Dispatch<SetStateAction<T>>,
    normalize: Normalize<T>,
): void => {
    const serialized = JSON.stringify(normalize(defaultValue))
    useEffect(() => {
        setSelected(normalize(defaultValue))
    }, [serialized])
}
